import { FC } from 'react';

import { FormikProvider, useFormik } from 'formik';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import {
  BADGE_TEXT_MAX_LENGTH,
  CASINO_BUTTON_TEXT_MAX_LENGTH,
  SpinBannerForm as SpinBannerFormValues,
  TOP_LABEL_TEXT_MAX_LENGTH,
  validationSchema,
} from '../form';
import { SpinBannerPreview } from '../spinBannerPreview/SpinBannerPreview';
import { DESCRIPTION_MAX_LENGTH, getRichTextTextLength } from '../utils';

import css from './SpinBannerForm.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';

interface SpinBannerFormProps {
  initialValues: SpinBannerFormValues;
  isSubmitting: boolean;
  onSubmit: (values: SpinBannerFormValues) => void;
}

export const SpinBannerForm: FC<SpinBannerFormProps> = ({ initialValues, isSubmitting, onSubmit }) => {
  const formik = useFormik<SpinBannerFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    dirty,
    submitCount,
  } = formik;

  const descriptionLength = getRichTextTextLength(values.description ?? '');

  const showFieldError = (field: keyof SpinBannerFormValues) =>
    Boolean(errors[field] && (touched[field] || submitCount > 0));

  return (
    <FormikProvider value={formik}>
      <div className={css.layout}>
        <form className={css.formColumn} onSubmit={handleSubmit}>
          <FileSelector
            label="Partner Logo"
            value={values.logoId}
            onSelect={(value) => {
              setFieldValue('logoId', value, true);
              void setFieldTouched('logoId', true);
            }}
            error={showFieldError('logoId') ? errors.logoId : undefined}
            required
          />

          <TextField
            name="casinoName"
            label="Casino Name"
            value={values.casinoName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.casinoName && errors.casinoName)}
            helperText={touched.casinoName && errors.casinoName}
            required
          />

          <TextField
            name="casinoButtonLink"
            label="Casino Button Link"
            value={values.casinoButtonLink}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.casinoButtonLink && errors.casinoButtonLink)}
            helperText={touched.casinoButtonLink && errors.casinoButtonLink}
            required
          />

          <TextField
            name="casinoButtonText"
            label="Casino Button Text"
            value={values.casinoButtonText}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.casinoButtonText && errors.casinoButtonText)}
            helperText={
              (touched.casinoButtonText && errors.casinoButtonText) ||
              `${values.casinoButtonText.length}/${CASINO_BUTTON_TEXT_MAX_LENGTH}`
            }
            slotProps={{ htmlInput: { maxLength: CASINO_BUTTON_TEXT_MAX_LENGTH } }}
            required
          />

          <TextField
            name="topLabelText"
            label="Top Label Text (optional)"
            value={values.topLabelText}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.topLabelText && errors.topLabelText)}
            helperText={
              (touched.topLabelText && errors.topLabelText) ||
              `${values.topLabelText.length}/${TOP_LABEL_TEXT_MAX_LENGTH}`
            }
            slotProps={{ htmlInput: { maxLength: TOP_LABEL_TEXT_MAX_LENGTH } }}
          />

          <TextField
            name="badgeText"
            label="Badge Text (optional)"
            value={values.badgeText}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.badgeText && errors.badgeText)}
            helperText={
              (touched.badgeText && errors.badgeText) || `${values.badgeText.length}/${BADGE_TEXT_MAX_LENGTH}`
            }
            slotProps={{ htmlInput: { maxLength: BADGE_TEXT_MAX_LENGTH } }}
          />

          <div>
            <RichTextarea
              label={`Description (${descriptionLength}/${DESCRIPTION_MAX_LENGTH})`}
              value={values.description}
              onChange={(value) => setFieldValue('description', value, true)}
              required
            />
            {showFieldError('description') && typeof errors.description === 'string' && (
              <p className={css.formFieldError}>{errors.description}</p>
            )}
          </div>

          <div>
            <RichTextarea
              label="Promocode Reward Description"
              value={values.promocodeRewardDescription}
              onChange={(value) => setFieldValue('promocodeRewardDescription', value, true)}
              required
            />
            {showFieldError('promocodeRewardDescription') && typeof errors.promocodeRewardDescription === 'string' && (
              <p className={css.formFieldError}>{errors.promocodeRewardDescription}</p>
            )}
          </div>

          <Button type="submit" variant="contained" loading={isSubmitting} disabled={!dirty}>
            Update
          </Button>
        </form>

        <div>
          <SpinBannerPreview />
        </div>
      </div>
    </FormikProvider>
  );
};
